<script setup lang="ts">
  import {ArrowDown} from '@element-plus/icons-vue';
  import type {MenuNode} from '../mock';

  const props = defineProps<{
    nodes: MenuNode[];
    selectedId: string;
    expandedMap: Record<string, boolean>;
    depth?: number;
  }>();

  const emit = defineEmits<{
    (e: 'select', id: string): void;
    (e: 'toggle', id: string): void;
  }>();

  const COLLAPSE_DURATION = '0.3s';

  function sortNodes(list: MenuNode[]) {
    return (list || []).slice().sort(function (a, b) {
      return (a.sort || 0) - (b.sort || 0);
    });
  }

  /** 三级：depth 0 产品线、1 系列 → 父节点；depth ≥ 2 设备 → 可选叶子 */
  function isBranch(node: MenuNode) {
    const d = props.depth || 0;
    if (d < 2) {
      return true;
    }
    return !!(node.children && node.children.length);
  }

  function isExpanded(id: string) {
    return !!props.expandedMap[id];
  }

  function onCollapseEnter(el: Element) {
    const target = el as HTMLElement;
    target.style.height = '0';
    target.style.overflow = 'hidden';
    target.offsetHeight;
    target.style.transition = 'height ' + COLLAPSE_DURATION + ' ease';
    target.style.height = target.scrollHeight + 'px';
  }

  function onCollapseAfterEnter(el: Element) {
    const target = el as HTMLElement;
    target.style.height = '';
    target.style.overflow = '';
    target.style.transition = '';
  }

  function onCollapseLeave(el: Element) {
    const target = el as HTMLElement;
    target.style.height = target.scrollHeight + 'px';
    target.style.overflow = 'hidden';
    target.offsetHeight;
    target.style.transition = 'height ' + COLLAPSE_DURATION + ' ease';
    target.style.height = '0';
  }

  function onCollapseAfterLeave(el: Element) {
    const target = el as HTMLElement;
    target.style.height = '';
    target.style.overflow = '';
    target.style.transition = '';
  }
</script>

<template>
  <div
    v-for="node in sortNodes(nodes)"
    :key="node.id"
    class="menu-node"
  >
    <template v-if="isBranch(node)">
      <div
        class="device-series-header"
        :class="{ expanded: isExpanded(node.id) }"
        :style="{ paddingLeft: (8 + (depth || 0) * 12) + 'px' }"
        @click="emit('toggle', node.id)"
      >
        <span class="series-name">{{ node.name }}</span>
        <el-icon class="series-arrow">
          <ArrowDown/>
        </el-icon>
      </div>
      <Transition
        @enter="onCollapseEnter"
        @after-enter="onCollapseAfterEnter"
        @leave="onCollapseLeave"
        @after-leave="onCollapseAfterLeave"
      >
        <div v-if="isExpanded(node.id)" class="device-series-children">
          <DeviceMenuTree
            :nodes="node.children || []"
            :selected-id="selectedId"
            :expanded-map="expandedMap"
            :depth="(depth || 0) + 1"
            @select="emit('select', $event)"
            @toggle="emit('toggle', $event)"
          />
        </div>
      </Transition>
    </template>
    <div
      v-else
      class="device-type-item"
      :class="{ active: selectedId === node.id }"
      :style="{ paddingLeft: (16 + (depth || 0) * 12) + 'px' }"
      @click="emit('select', node.id)"
    >
      {{ node.name }}
    </div>
  </div>
</template>

<style scoped lang="scss">
  .menu-node {
    margin-bottom: 2px;
  }

  .device-series-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 48px;
    padding-right: 12px;
    cursor: pointer;
    color: var(--color-text-regular, #606266);
    font-size: 15px;
    font-weight: 500;
    border-radius: 5px;

    &.expanded {
      .series-arrow {
        transform: rotate(180deg);
      }
    }

    .series-name {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      padding-right: 8px;
    }

    .series-arrow {
      flex-shrink: 0;
      font-size: 12px;
      transition: transform 0.3s ease;
      color: inherit;
    }
  }

  .device-series-children {
    overflow: hidden;
  }

  .device-type-item {
    display: flex;
    align-items: center;
    height: 44px;
    padding-right: 16px;
    cursor: pointer;
    color: var(--color-text-regular, #606266);
    font-size: 14px;
    border-radius: 5px;
    transition: background-color 0.2s ease;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    user-select: none;

    &:hover {
      background: var(--background-color-primary, #f5f8ff);
    }

    &.active {
      background: var(--background-color-primary, #f5f8ff);
      color: var(--color-primary, #1677ff);
      font-weight: 500;
    }
  }
</style>
